import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

interface EmailVerificationProps {
  email?: string;
  onBack?: () => void;
}

export const EmailVerification: React.FC<EmailVerificationProps> = ({ email, onBack }) => {
  const [verificationEmail, setVerificationEmail] = useState(email || '');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const sendVerificationEmail = async () => {
    if (!verificationEmail) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa tu email',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: verificationEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth?verified=true`
        }
      });

      if (error) throw error;

      setSent(true);
      toast({
        title: 'Email enviado',
        description: 'Te hemos enviado un enlace de verificación a tu email',
      });
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo enviar el email de verificación',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle>Email enviado</CardTitle>
          <CardDescription>
            Hemos enviado un enlace de verificación a <strong>{verificationEmail}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center text-sm text-muted-foreground">
            <p>Revisa tu bandeja de entrada y haz clic en el enlace para verificar tu cuenta.</p>
            <p className="mt-2">Si no ves el email, revisa tu carpeta de spam.</p>
          </div>
          <div className="flex flex-col space-y-2">
            <Button onClick={sendVerificationEmail} variant="outline" disabled={loading}>
              Reenviar email
            </Button>
            {onBack && (
              <Button onClick={onBack} variant="ghost">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
          <Mail className="h-6 w-6 text-blue-600" />
        </div>
        <CardTitle>Verificar Email</CardTitle>
        <CardDescription>
          Ingresa tu email para recibir un enlace de verificación
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={verificationEmail}
            onChange={(e) => setVerificationEmail(e.target.value)}
            placeholder="tu-email@ejemplo.com"
          />
        </div>
        <div className="flex flex-col space-y-2">
          <Button onClick={sendVerificationEmail} disabled={loading}>
            {loading ? 'Enviando...' : 'Enviar enlace de verificación'}
          </Button>
          {onBack && (
            <Button onClick={onBack} variant="ghost">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};